import { Button } from "components/Button";
import { Modal } from "components/Modal";
import { useToast } from "hooks/useToast";
import Papa from "papaparse";
import React, { useRef, useState } from "react";
import styled from "styled-components";

const MAX_ROWS = 5000;

interface ImportCsvModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (registrations: RegistrationImport[]) => void;
  isLoading: boolean;
}

export interface RegistrationImport {
  customerCpf?: string;
  profile: {
    name: string;
    company: string;
    position: string;
    bio: string;
    isSpeaker: boolean;
    isVisible: boolean;
    displayPosition?: number;
  };
  importData?: {
    cpf?: string;
    email?: string;
    phone?: string;
  };
}

// Fields available for mapping
const MAPPING_FIELDS = [
  { key: "customerCpf", label: "CPF" },
  { key: "profile.name", label: "Nome" },
  { key: "profile.company", label: "Empresa" },
  { key: "profile.position", label: "Cargo" },
  { key: "profile.bio", label: "Biografia" },
  { key: "profile.isSpeaker", label: "É Palestrante" },
  { key: "profile.isVisible", label: "É Visível" },
  { key: "profile.displayPosition", label: "Ordem de Exibição" },
  { key: "importData.email", label: "Email" },
  { key: "importData.phone", label: "Telefone" },
];

export const ImportCsvModal: React.FC<ImportCsvModalProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
}) => {
  const { setToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [fileName, setFileName] = useState<string>("");

  const resetState = () => {
    setCsvHeaders([]);
    setCsvData([]);
    setFieldMapping({});
    setFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);

    Papa.parse(file, {
      skipEmptyLines: true,
      encoding: "UTF-8",
      complete: (results) => {
        const data = results.data as string[][];
        if (data.length === 0) {
          setCsvHeaders([]);
          setCsvData([]);
          return;
        }

        // First row is headers, rest is data
        const headers = data[0].map((h) => h?.trim() || "");
        const rows = data
          .slice(1)
          .map((row) => row.map((cell) => cell?.trim() || ""));

        // Check if rows exceed the limit
        if (rows.length > MAX_ROWS) {
          setToast({
            type: "error",
            message: `O arquivo contém ${rows.length.toLocaleString()} registros. O limite máximo é ${MAX_ROWS.toLocaleString()} registros.`,
            isActive: true,
            timeToRemoveToast: 5000,
          });
          setCsvHeaders([]);
          setCsvData([]);
          setFileName("");
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          onClose();
          return;
        }

        setCsvHeaders(headers);
        setCsvData(rows);
        setFieldMapping({});
      },
      error: (error) => {
        console.error("Error parsing CSV:", error);
        setToast({
          type: "error",
          message:
            "Erro ao processar o arquivo CSV. Verifique o formato do arquivo.",
          isActive: true,
          timeToRemoveToast: 5000,
        });
        setCsvHeaders([]);
        setCsvData([]);
        onClose();
      },
    });
  };

  const handleMappingChange = (csvColumn: string, fieldKey: string) => {
    setFieldMapping((prev) => {
      const newMapping = { ...prev };

      // Remove previous mapping for this field if exists
      Object.keys(newMapping).forEach((key) => {
        if (newMapping[key] === fieldKey) {
          delete newMapping[key];
        }
      });

      // Set new mapping
      if (fieldKey) {
        newMapping[csvColumn] = fieldKey;
      } else {
        delete newMapping[csvColumn];
      }

      return newMapping;
    });
  };

  const getUsedFields = (): string[] => {
    return Object.values(fieldMapping);
  };

  const getAvailableFieldsFor = (csvColumn: string): typeof MAPPING_FIELDS => {
    const usedFields = getUsedFields();
    const currentMapping = fieldMapping[csvColumn];

    return MAPPING_FIELDS.filter(
      (field) => !usedFields.includes(field.key) || field.key === currentMapping
    );
  };

  const setNestedValue = (obj: any, path: string, value: any) => {
    const keys = path.split(".");
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
  };

  const parseValue = (value: string, fieldKey: string): any => {
    if (fieldKey === "profile.isSpeaker" || fieldKey === "profile.isVisible") {
      const lowerValue = value.toLowerCase();
      return (
        lowerValue === "true" || lowerValue === "sim" || lowerValue === "1"
      );
    }
    if (fieldKey === "profile.displayPosition") {
      const num = parseInt(value, 10);
      return isNaN(num) ? undefined : num;
    }
    return value;
  };

  const buildRegistrations = (): RegistrationImport[] => {
    return csvData
      .filter((row) => row.some((cell) => cell.trim() !== ""))
      .map((row) => {
        const registration: any = {
          customerCpf: "",
          profile: {
            name: "",
            company: "",
            position: "",
            bio: "",
            isSpeaker: false,
            isVisible: false,
          },
          importData: {},
        };

        csvHeaders.forEach((header, index) => {
          const fieldKey = fieldMapping[header];
          if (fieldKey && row[index] !== undefined) {
            const value = parseValue(row[index], fieldKey);
            setNestedValue(registration, fieldKey, value);
          }
        });

        if (registration.customerCpf) {
          registration.importData.cpf = registration.customerCpf;
        }

        return registration as RegistrationImport;
      });
  };

  const handleConfirm = () => {
    const registrations = buildRegistrations();
    onConfirm(registrations);
  };

  const hasRequiredMappings = () => {
    const usedFields = getUsedFields();
    return usedFields.includes("profile.name");
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalContainer>
        <Title>Importar Inscrições via CSV</Title>

        <Section>
          <Label>1. Selecione o arquivo CSV</Label>
          <FileInputWrapper>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <Button
              label="Escolher arquivo"
              typeBtn="outline"
              onClick={() => fileInputRef.current?.click()}
              type="button"
            />
            {fileName && <FileName>{fileName}</FileName>}
          </FileInputWrapper>
        </Section>

        {csvHeaders.length > 0 && (
          <Section>
            <Label>2. Mapeie as colunas do CSV para os campos</Label>
            <HelpText>Campo obrigatório: Nome</HelpText>
            <MappingTable>
              <thead>
                <tr>
                  <th>Coluna do CSV</th>
                  <th>Campo do Sistema</th>
                </tr>
              </thead>
              <tbody>
                {csvHeaders.map((header) => (
                  <tr key={header}>
                    <td>{header}</td>
                    <td>
                      <Select
                        value={fieldMapping[header] || ""}
                        onChange={(e) =>
                          handleMappingChange(header, e.target.value)
                        }
                      >
                        <option value="">-- Não mapear --</option>
                        {getAvailableFieldsFor(header).map((field) => (
                          <option key={field.key} value={field.key}>
                            {field.label}
                          </option>
                        ))}
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </MappingTable>
            <DataPreview>
              <strong>Registros encontrados:</strong>{" "}
              {csvData.filter((row) => row.some((cell) => cell.trim())).length}
            </DataPreview>
          </Section>
        )}

        <ButtonsContainer>
          <Button
            label="Cancelar"
            typeBtn="outline"
            onClick={handleClose}
            type="button"
          />
          <Button
            label="Confirmar Importação"
            typeBtn="blue"
            onClick={handleConfirm}
            type="button"
            disable={!hasRequiredMappings() || isLoading}
            load={isLoading}
          />
        </ButtonsContainer>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled.div`
  padding: 1.5rem;
  min-width: 500px;
  max-width: 700px;
  max-height: 80vh;
  overflow-y: auto;
  background: #fff;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  color: #3a3956;
  margin-bottom: 1.5rem;
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #3a3956;
  margin-bottom: 0.5rem;
`;

const HelpText = styled.p`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.75rem;
`;

const FileInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const FileName = styled.span`
  color: #666;
  font-size: 0.875rem;
`;

const MappingTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
  }

  th {
    background-color: #f5f5f5;
    font-weight: 600;
    color: #3a3956;
  }

  td:first-child {
    font-weight: 500;
    color: #333;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #312cc1;
  }
`;

const DataPreview = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #f0f4ff;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #312cc1;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
`;
